/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Fleet health's settings drawer (docs/CONTRACT-fleet-governance.md sections 6 and 12.2): the status thresholds, the
 * retention, push flapping, policy behind, each ring's current-version override (the OTA value as its help), and Alerts to
 * IT (the stub: nothing is sent). Edited as one form and saved as only what changed (PUT /v1/admin/fleet/settings, audited
 * `admin.fleet_settings_changed`). Auditors see it read-only.
 */
import { useEffect, useState } from 'react';
import {
  FLEET_NUMBER_FIELDS, NOTIFY_IT_HELP, RINGS, fleetApi, fleetFailure, fleetForm, fleetPatch, patchKeys, ringLabel, validateFleet,
  type FleetNumberKey, type FleetSettingsForm, type FleetSettingsView, type Ring,
} from '../../lib/fleet';
import { Callout, Drawer, Field, Grid, Input, NumberInput, SaveBar, Skeleton, Switch } from '../../ui';
import { dateTime } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { LoadError } from './parts';

/** Mounted only while open, so the settings are read when the drawer opens and never by a view that does not open it. */
export function FleetSettingsDrawer({ open, canEdit, onClose }: { open: boolean; canEdit: boolean; onClose: () => void }) {
  return open ? <SettingsDrawer canEdit={canEdit} onClose={onClose} /> : null;
}

function overrideHelp(view: FleetSettingsView, ring: Ring): string {
  const current = view.currentVersions?.[ring];
  if (current?.source === 'ota' && current.version) {
    return `From OTA: ${current.version}${current.publishedAt ? `, published ${dateTime(current.publishedAt)}` : ''}. Set a version only for a ring updated outside Gyro's OTA; empty uses OTA.`;
  }
  if (current?.source === 'override' && current.version) return `Set here: ${current.version}. Empty it to use the newest OTA release that reaches the ring.`;
  return "No OTA release reaches this ring yet, so its current version is unknown unless one is set here.";
}

function SettingsDrawer({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const { load, reload, reloading, setData } = useLoad(() => fleetApi.settings(), 'fleet-settings');
  const view = load.state === 'ready' ? load.data : null;
  const [form, setForm] = useState<FleetSettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (view) setForm(fleetForm(view.settings));
  }, [view]);

  const patch = view && form ? fleetPatch(form, view.settings) : {};
  const changed = patchKeys(patch);
  const setNumber = (key: FleetNumberKey, value: string) => {
    setSaved(null);
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };
  const setOverride = (ring: Ring, value: string) => {
    setSaved(null);
    setForm((current) => (current ? { ...current, override: { ...current.override, [ring]: value } } : current));
  };

  const save = async () => {
    if (!view || !form) return;
    const problem = validateFleet(form);
    if (problem) {
      setSaveError(problem);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const next = await fleetApi.saveSettings(patch);
      setData(() => next);
      setSaved(`Fleet health settings saved: ${changed.join(', ')}. The change is in the audit trail.`);
    } catch (error) {
      setSaveError(fleetFailure(error, 'The settings could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Fleet health settings"
      description="When a PC reads Recent, Stale, Not seen or Gone, what counts as a problem, and the alerts to IT"
      className="ui-drawer--wide"
      footer={canEdit && view && form ? (
        <SaveBar
          changes={changed.length}
          saving={saving}
          error={saveError}
          saved={saved}
          onSave={() => void save()}
          onDiscard={() => { setForm(fleetForm(view.settings)); setSaveError(null); }}
        />
      ) : undefined}
    >
      {load.state === 'loading' ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div> : null}
      {load.state === 'failed' ? <LoadError title="The Fleet health settings could not be read" error={load.error} words={(error) => fleetFailure(error)} onRetry={reload} retrying={reloading} /> : null}
      {view && form ? (
        <div className="ui-form">
          {!canEdit ? <Callout tone="info">Read-only: auditors see the settings; only IT admins change them.</Callout> : null}
          <Grid cols={2}>
            {FLEET_NUMBER_FIELDS.map((field) => (
              <Field key={field.key} label={field.label} help={field.help}>
                <NumberInput aria-label={field.label} value={form[field.key]} disabled={!canEdit} onChange={(event) => setNumber(field.key, event.target.value)} />
              </Field>
            ))}
          </Grid>
          <div className="ui-section">
            <h3 className="ui-section-title">Each ring's current version</h3>
            <Grid cols={3}>
              {RINGS.map((ring) => (
                <Field key={ring} label={`${ringLabel(ring)} current version`} help={overrideHelp(view, ring)}>
                  <Input aria-label={`${ringLabel(ring)} current version`} className="is-mono" placeholder="from OTA" value={form.override[ring]} disabled={!canEdit} onChange={(event) => setOverride(ring, event.target.value)} />
                </Field>
              ))}
            </Grid>
          </div>
          <div className="ui-section">
            <h3 className="ui-section-title">Alerts to IT</h3>
            <Switch
              label="Alerts to IT"
              checked={form.notifyIt}
              disabled={!canEdit}
              onChange={(value) => { setSaved(null); setForm((current) => (current ? { ...current, notifyIt: value } : current)); }}
              stateLabels={['On', 'Off']}
            />
            <p className="ui-para ui-quiet">{NOTIFY_IT_HELP}</p>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
